import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import _omit from 'lodash/omit';
import _includes from 'lodash/includes';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
// Custom
import {
  getEquipmentsList,
  getInstallationDoors,
  getInstallationSources,
  getInstallationDevices,
  getInstallationScenarios,
  getInstallationPlannings,
} from 'actions';
import { getHocDisplayName, dispatchWithCache, invoke } from 'utils/Tools';
import withPermissions from 'hocs/WithPermissions';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';

// Extract needed permissions
const { pInstallation: { configRead, planningRead } } = permissions;

const withResources = (getId, resources) => (WrappedComponent) => {
  const needResource = (resource) => _includes(resources, resource);

  class WithResources extends React.Component {
    constructor(props) {
      super(props);

      this.doForceReload = this.doForceReload.bind(this);

      // Prepare the props that don't need to be passed to wrapped component
      this.omittedProps = [
        'cache', 'hasPermission',
        'loadingEquipments', 'loadingDoors', 'loadingSources', 'loadingDevices', 'loadingScenarios', 'loadingPlannings',
        'handleGetEquipments', 'handleGetDoors', 'handleGetSources', 'handleGetDevices', 'handleGetScenarios', 'handleGetPlannings',
      ];

      // Resources loaded flags
      // Config
      this.equipmentsLoaded = false;
      this.doorsLoaded = false;
      this.sourcesLoaded = false;
      this.devicesLoaded = false;
      this.scenariosLoaded = false;
      // Plannings
      this.planningsLoaded = false;

      // Permissions
      const { cache, hasPermission } = this.props;

      this.state = {
        loading:
          (!cache.equipments && needResource('equipments') && hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' }))
          || (!cache.doors && needResource('doors') && hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' }))
          || (!cache.sources && needResource('sources') && hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' }))
          || (!cache.devices && needResource('devices') && hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' }))
          || (!cache.scenarios && needResource('scenarios') && hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' }))
          || (!cache.plannings && needResource('plannings') && hasPermission({ needed: planningRead, permissionsOrContext: 'installationPermissions' })),
      };
    }

    componentDidMount() {
      // Get installation id
      const { id } = getId(this.props);

      const {
        hasPermission,
        handleGetEquipments, handleGetDoors, handleGetSources, handleGetDevices, handleGetScenarios, handleGetPlannings,
      } = this.props;

      // Config
      if (hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' })) {
        if (needResource('equipments')) {
          this.equipmentsLoaded = handleGetEquipments();
        }
        if (needResource('doors')) {
          this.doorsLoaded = handleGetDoors(id);
        }
        if (needResource('sources')) {
          this.sourcesLoaded = handleGetSources(id);
        }
        if (needResource('devices')) {
          this.devicesLoaded = handleGetDevices(id);
        }
        if (needResource('scenarios')) {
          this.scenariosLoaded = handleGetScenarios(id);
        }
      } else {
        this.equipmentsLoaded = true;
        this.doorsLoaded = true;
        this.sourcesLoaded = true;
        this.devicesLoaded = true;
        this.scenariosLoaded = true;
      }
      // Plannings
      if (hasPermission({ needed: planningRead, permissionsOrContext: 'installationPermissions' })) {
        if (needResource('plannings')) {
          this.planningsLoaded = handleGetPlannings(id);
        }
      } else {
        this.planningsLoaded = true;
      }
    }

    componentDidUpdate(prevProps) {
      const { loading } = this.state;

      // Performs checks only if loading is in progress
      if (loading) {
        const {
          loadingEquipments, loadingDoors, loadingSources, loadingDevices, loadingScenarios, loadingPlannings,
        } = this.props;
        const {
          loadingEquipments: prevLoadingEquipments,
          loadingDoors: prevLoadingDoors,
          loadingSources: prevLoadingSources,
          loadingDevices: prevLoadingDevices,
          loadingScenarios: prevLoadingScenarios,
          loadingPlannings: prevLoadingPlannings,
        } = prevProps;

        // Check if all resources needed are loaded
        this.equipmentsLoaded = this.equipmentsLoaded || (!loadingEquipments && prevLoadingEquipments);
        this.doorsLoaded = this.doorsLoaded || (!loadingDoors && prevLoadingDoors);
        this.sourcesLoaded = this.sourcesLoaded || (!loadingSources && prevLoadingSources);
        this.devicesLoaded = this.devicesLoaded || (!loadingDevices && prevLoadingDevices);
        this.scenariosLoaded = this.scenariosLoaded || (!loadingScenarios && prevLoadingScenarios);
        this.planningsLoaded = this.planningsLoaded || (!loadingPlannings && prevLoadingPlannings);

        let allResourcesLoaded = true;
        if (needResource('equipments') && !this.equipmentsLoaded) {
          allResourcesLoaded = false;
        }
        if (needResource('doors') && !this.doorsLoaded) {
          allResourcesLoaded = false;
        }
        if (needResource('sources') && !this.sourcesLoaded) {
          allResourcesLoaded = false;
        }
        if (needResource('devices') && !this.devicesLoaded) {
          allResourcesLoaded = false;
        }
        if (needResource('scenarios') && !this.scenariosLoaded) {
          allResourcesLoaded = false;
        }
        if (needResource('plannings') && !this.planningsLoaded) {
          allResourcesLoaded = false;
        }

        // All resources are loaded => update loading flag
        if (allResourcesLoaded) {
          invoke(() => {
            this.setState({
              loading: false,
            });
          });
        }
      }
    }

    // Private
    doForceReload(forcedResources) {
      // Get installation id
      const { id } = getId(this.props);

      const {
        hasPermission,
        handleGetEquipments, handleGetDoors, handleGetSources, handleGetDevices, handleGetScenarios, handleGetPlannings,
      } = this.props;

      // Config
      if (hasPermission({ needed: configRead, permissionsOrContext: 'installationPermissions' })) {
        if (_includes(forcedResources, 'equipments')) {
          handleGetEquipments(false);
        }
        if (_includes(forcedResources, 'doors')) {
          handleGetDoors(id, false);
        }
        if (_includes(forcedResources, 'sources')) {
          handleGetSources(id, false);
        }
        if (_includes(forcedResources, 'devices')) {
          handleGetDevices(id, false);
        }
        if (_includes(forcedResources, 'scenarios')) {
          handleGetScenarios(id, false);
        }
      }
      // Plannings
      if (hasPermission({ needed: planningRead, permissionsOrContext: 'installationPermissions' })) {
        if (_includes(forcedResources, 'plannings')) {
          handleGetPlannings(id, false);
        }
      }
    }

    render() {
      const { loading } = this.state;

      return (
        <LoadingDisplayer loading={loading}>
          <WrappedComponent
            handleForceReload={this.doForceReload}
            {..._omit(this.props, this.omittedProps)}
          />
        </LoadingDisplayer>
      );
    }
  }

  WithResources.displayName = getHocDisplayName('WithResources', WrappedComponent);

  WithResources.propTypes = {
    cache: PropTypes.shape({
      equipments: PropTypes.bool.isRequired,
      doors: PropTypes.bool.isRequired,
      sources: PropTypes.bool.isRequired,
      devices: PropTypes.bool.isRequired,
      scenarios: PropTypes.bool.isRequired,
      plannings: PropTypes.bool.isRequired,
    }).isRequired,
    loadingEquipments: PropTypes.bool.isRequired,
    loadingDoors: PropTypes.bool.isRequired,
    loadingSources: PropTypes.bool.isRequired,
    loadingDevices: PropTypes.bool.isRequired,
    loadingScenarios: PropTypes.bool.isRequired,
    loadingPlannings: PropTypes.bool.isRequired,
    hasPermission: PropTypes.func.isRequired,
    handleGetEquipments: PropTypes.func.isRequired,
    handleGetDoors: PropTypes.func.isRequired,
    handleGetSources: PropTypes.func.isRequired,
    handleGetDevices: PropTypes.func.isRequired,
    handleGetScenarios: PropTypes.func.isRequired,
    handleGetPlannings: PropTypes.func.isRequired,
  };

  const mapStateToProps = (state) => ({
    cache: state.installation.cache,
    loadingEquipments: state.equipment.list.loading,
    loadingDoors: state.installationDoor.list.loading,
    loadingSources: state.installationSource.list.loading,
    loadingDevices: state.installationDevice.list.loading,
    loadingScenarios: state.installationScenario.list.loading,
    loadingPlannings: state.installationPlanning.list.loading,
  });

  const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const { cache } = stateProps;
    const { dispatch } = dispatchProps;

    return {
      ...ownProps,
      ...stateProps,
      handleGetEquipments: (useCache = true) => {
        dispatchWithCache(cache.equipments && useCache, () => dispatch(getEquipmentsList()));
        return cache.equipments;
      },
      handleGetDoors: (id, useCache = true) => {
        dispatchWithCache(cache.doors && useCache, () => dispatch(getInstallationDoors(id)));
        return cache.doors;
      },
      handleGetSources: (id, useCache = true) => {
        dispatchWithCache(cache.sources && useCache, () => dispatch(getInstallationSources(id)));
        return cache.sources;
      },
      handleGetDevices: (id, useCache = true) => {
        dispatchWithCache(cache.devices && useCache, () => dispatch(getInstallationDevices(id)));
        return cache.devices;
      },
      handleGetScenarios: (id, useCache = true) => {
        dispatchWithCache(cache.scenarios && useCache, () => dispatch(getInstallationScenarios(id)));
        return cache.scenarios;
      },
      handleGetPlannings: (id, useCache = true) => {
        dispatchWithCache(cache.plannings && useCache, () => dispatch(getInstallationPlannings(id)));
        return cache.plannings;
      },
    };
  };

  return compose(
    connect(
      mapStateToProps,
      null,
      mergeProps,
    ),
    withPermissions,
  )(WithResources);
};

export default withResources;



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/hocs/WithResources.js